import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../app/AppContext';
import { TrendChart } from '../components/TrendChart';
import type { ModuleId } from '../core/types';
import { buildModuleScorePoints, buildOverallScorePoints, filterScorePointsByDate } from '../training';

const moduleIds: ModuleId[] = ['read-switch', 'central-peripheral', 'player-track', 'moving-target'];
const moduleLabels: Record<ModuleId, string> = {
  'read-switch': '動きの識別',
  'central-peripheral': '中央・周辺の同時判断',
  'player-track': '複数対象の追跡',
  'moving-target': '動く記号',
};
const legacyModeLabels: Record<string, string> = {
  familiarization: '旧お試し',
  'baseline-a': '旧記録',
  'baseline-b': '旧記録',
  training: '練習',
  reassessment: '旧記録',
  retention: '旧記録',
};

function localDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function HistoryPage() {
  const app = useApp();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const allSeries = useMemo(() => ({
    overall: buildOverallScorePoints(app.sessions, app.summariesBySession),
    modules: Object.fromEntries(moduleIds.map((moduleId) => [moduleId, buildModuleScorePoints(moduleId, app.sessions, app.summariesBySession)])) as Record<ModuleId, ReturnType<typeof buildModuleScorePoints>>,
  }), [app.sessions, app.summariesBySession]);
  const overall = filterScorePointsByDate(allSeries.overall, from, to);
  const modules = Object.fromEntries(moduleIds.map((moduleId) => [moduleId, filterScorePointsByDate(allSeries.modules[moduleId], from, to)])) as typeof allSeries.modules;
  const sessionInPeriod = (date: string) => filterScorePointsByDate([{
    sessionId: '', date, value: 0, environmentConditionId: '', protocolVersion: '',
  }], from, to).length > 0;
  const visibleSessions = app.sessions.filter((session) => sessionInPeriod(session.completedAt ?? session.startedAt));

  const chooseRecentPeriod = (days: number) => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - days + 1);
    setFrom(localDateString(start));
    setTo(localDateString(end));
  };

  return (
    <div className="page">
      <header className="page-heading"><div><div className="eyebrow">あなたの記録</div><h1>スコアの時系列</h1><p>総合点と4課題の変化を、見たい期間に絞って確認できます。</p></div></header>

      <section className="panel history-period" aria-label="表示期間">
        <div><span className="panel-label">表示期間</span><strong>{from || to ? `${from || '最初'} 〜 ${to || '今日'}` : 'すべての期間'}</strong></div>
        <div className="history-presets" aria-label="期間の候補">
          <button type="button" className="filter-chip" onClick={() => chooseRecentPeriod(30)}>30日</button>
          <button type="button" className="filter-chip" onClick={() => chooseRecentPeriod(90)}>90日</button>
          <button type="button" className="filter-chip" onClick={() => chooseRecentPeriod(365)}>1年</button>
          <button type="button" className="filter-chip" onClick={() => { setFrom(''); setTo(''); }}>全期間</button>
        </div>
        <div className="history-date-inputs">
          <label>開始日<input type="date" value={from} max={to || undefined} onChange={(event) => setFrom(event.target.value)} /></label>
          <span aria-hidden="true">—</span>
          <label>終了日<input type="date" value={to} min={from || undefined} onChange={(event) => setTo(event.target.value)} /></label>
        </div>
      </section>

      <section className="history-score-summary" aria-label="期間内の最新スコア">
        <div className="history-score-total"><span>総合点</span><strong>{overall.length ? Math.round(overall.at(-1)!.value) : '—'}</strong><small>/ 100</small></div>
        {moduleIds.map((moduleId) => <div key={moduleId}><span>{moduleLabels[moduleId]}</span><strong>{modules[moduleId].length ? Math.round(modules[moduleId].at(-1)!.value) : '—'}</strong><small>/ 100</small></div>)}
      </section>

      <section className="history-trends" aria-label="スコアの推移">
        <TrendChart label="総合点" points={overall} emphasized />
        {moduleIds.map((moduleId) => <TrendChart key={moduleId} label={moduleLabels[moduleId]} points={modules[moduleId]} />)}
      </section>

      <div className="section-row"><h2>実施した記録</h2><span className="status-label">このパソコン内に保存</span></div>
      <section className="panel">
        <div className="panel-label">新しい順・{visibleSessions.length}件</div>
        {visibleSessions.length ? (
          <table className="history-table">
            <thead><tr><th>日時</th><th>種類</th><th>状態</th><th>課題</th><th>疲労</th><th>結果</th></tr></thead>
            <tbody>{visibleSessions.map((session) => {
              const result = session.status !== 'completed'
                ? '集計対象外'
                : session.mode === 'familiarization'
                  ? '採点なし'
                  : <Link to={`/results/${session.id}`}>詳細 →</Link>;
              return <tr key={session.id}><td>{new Intl.DateTimeFormat('ja-JP', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(session.startedAt))}</td><td><span className="tag">{legacyModeLabels[session.mode]}</span></td><td><span className={`tag ${session.status === 'completed' ? 'good' : 'warn'}`}>{session.status === 'completed' ? '完了' : session.status === 'aborted' ? '中断' : '進行中'}</span></td><td>{session.moduleIds.length === 1 ? moduleLabels[session.moduleIds[0]] : `${session.moduleIds.length}課題`}</td><td>{session.fatigue} / 5</td><td>{result}</td></tr>;
            })}</tbody>
          </table>
        ) : (
          <div className="empty-state"><div><strong>{app.sessions.length ? 'この期間の記録はありません。' : 'まだ記録はありません。'}</strong><p>{app.sessions.length ? '表示期間を広げると、以前の記録を確認できます。' : '4課題まとめて、または1課題を選んで始められます。'}</p><Link className="button" to={app.calibration ? '/session' : '/calibration'}>{app.sessions.length ? '練習する' : '開始する'}</Link></div></div>
        )}
      </section>
    </div>
  );
}
